import { IObjectsPool, SearchResult, IMapMngr } from './interfaces';
import { AspObject, AspObjectsSlot, ObjectType } from './AspObject';
import { MsgHandler } from '../msg/msgHandler';
import { AppConfig } from '../app.config';
import { SideNavigationService } from '../side-navigation/side-navigation.service';
import { AppInjector } from '../common/inject';
import { AroService } from '../aro/aro.service';
import * as aspSettings from './aspSettings';
import * as consts from '../common/consts';
import { DisplayObject } from './DisplayObject';
import { mainMapMngr } from './MapMngr';
import * as projMsgs from '../../../app/msg/projMsgs';
import { UserActionService } from '@core_n/user-action/user-action.service';


//import {AroTrackService} from '../../../app/aro/aro-track.service';
//import { TrackObject } from 'src/app/asp/TrackObject';

export interface ObjectsPoolSubscriber {
  onObjectCreate(obj: AspObject): DisplayObject;
  onObjectDelete(obj: AspObject): void;
  onObjectHook(obj: AspObject): void;
  onDeleteAllObjects(): void;
  onDeleteSlot(slotNumber: number): void;
}

export abstract class ObjectsPool extends MsgHandler implements IObjectsPool {

  protected initialized = false;
  protected objectsType: string;
  protected allObjects: Map<any, AspObject> = new Map();

  protected objectsSlots: Array<AspObjectsSlot> = null;
  protected currentSlot: number;

  private subscribers: Array<ObjectsPoolSubscriber> = new Array();

  constructor(protected objectType: ObjectType, msgType: number,
    protected slotsMaxHistory: number = 0 /* seconds */,
    protected _hookable = false, protected _clickable = false, protected _supportPicking = false,
  ) {
    super(msgType);
    this.objectsType = objectType;
    if (slotsMaxHistory > 0) {
      let slotsCount = slotsMaxHistory * Math.ceil(1000.0 / AppConfig.settings.map.refreshRate);
      this.currentSlot = 0;
      this.objectsSlots = new Array(slotsCount);
      for (let i = 0; i < slotsCount; i++)
        this.objectsSlots[i] = new AspObjectsSlot();
      this.objectsSlots[this.currentSlot].time = Date.now();
    }

    this.initialized = true;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getObjectType(): ObjectType {
    return this.objectType;
  }

  getObject(objectId): AspObject {
    let obj = this.allObjects.get(objectId);
    return obj === undefined ? null : obj;
  }

  getObjectsSlotsCount(): number {
    return this.objectsSlots && this.objectsSlots.length;
  }

  getCurrentSlot(): number {
    return this.currentSlot;
  }

  getObjectsSlot(slotNumer: number): AspObjectsSlot {
    if (this.objectsSlots && slotNumer < this.objectsSlots.length)
      return this.objectsSlots[slotNumer];
    else
      return null;
  }

  addObject(obj: AspObject) {
    this.allObjects.set(obj.id, obj);
    if (this.objectsSlots != null) {
      obj.slotNumber = this.currentSlot;
      this.objectsSlots[this.currentSlot].objects.push(obj);
    }
    this.notifyObjectCreate(obj);
  }

  deleteObject(obj: AspObject) {
    this.notifyObjectDelete(obj);
    obj.deactivate();
    this.allObjects.delete(obj.id);
    aspSettings.selectedIds.delete(obj.id);
  }

  toggleHookedObjectsHistory() {
    for (let id of aspSettings.selectedIds) {
      let obj: AspObject = this.getObject(id);
      if (obj != null)
        obj.setTrailVisibility(!obj.isTrailVisible());
    }
  }

  changeHostilityOfTracks(hostility: number) {
    if (this.objectType != ObjectType.Track)
      return;
    for (let id of aspSettings.selectedIds) {
      let obj: any = this.getObject(id);
      if (obj != null) {
        if (obj.hostility != hostility) {
          obj.userSetHostility = true;
          this.setTrackHostility(hostility, obj.trackID);
          obj.threatPoly = null;
          obj.ip = null;
          obj.update();
        }
      }
    }
  }

  forceSendPolyToSmart() {
    if (this.objectType != ObjectType.Track)
      return;
    for (let id of aspSettings.selectedIds) {
      let obj: any = this.getObject(id);
      if (obj != null) {
        if (!obj.HideSendToSmart) {
          let ForceSend = new projMsgs.ForceSendToSmart();
          ForceSend.TrackNumber = obj.trackID;
          AppInjector.get(UserActionService).sendUserAction(ForceSend);
        }
      }
    }
  }

  setTrackHostility(hostility: number, trackID: number) {
    let SetHostility = new projMsgs.SetHostility();
    SetHostility.TrackNumber = trackID;
    SetHostility.TrackHostility = hostility;
    AppInjector.get(UserActionService).sendUserAction(SetHostility);
  }

  private periodicCount = 0;

  // Periodic refresh, defined in IObjectsPool
  periodicUpdate(): boolean {
    if (!this.initialized)
      return false;

    if (this.objectsSlots != null) {
      // activate all objects in current slot
      for (let obj of this.objectsSlots[this.currentSlot].objects)
        obj.activate();

      this.currentSlot += 1;
      if (this.currentSlot == this.objectsSlots.length)
        this.currentSlot = 0;

      this.objectsSlots[this.currentSlot].time = Date.now();

      // delete all objects in current slot
      for (let obj of this.objectsSlots[this.currentSlot].objects)
        this.deleteObject(obj);
      this.objectsSlots[this.currentSlot].objects.length = 0;
      this.notifySlotDelete(this.currentSlot);
    }

    this.periodicCount++;
    if (this.periodicCount == 10) {
      // console.debug(this.objectsType, 'count:', this.allObjects.size);
      this.periodicCount = 0;
    }

    return true;
  }

  deleteAllObjects() {
    for (let i = 0; i < this.subscribers.length; i++)
      this.subscribers[i].onDeleteAllObjects();
    if (this.objectsSlots != null) {
      for (let i = 0; i < this.objectsSlots.length; i++) {
        if (this.objectsSlots[i] != null) {
          for (let obj of this.objectsSlots[i].objects)
            this.deleteObject(obj);
          this.objectsSlots[i].objects.length = 0;
        }
      }
    }
    else {
      this.allObjects.forEach((obj: AspObject) => {
        this.notifyObjectDelete(obj);
        obj.deactivate();
        aspSettings.selectedIds.delete(obj.id);
      });
      this.allObjects.clear();
    }
  }

  updateAllObjects() {
    this.allObjects.forEach(obj => {
      obj.notifyDataChange();
    })
  }

  hookable(): boolean {
    return this._hookable;
  }

  clickable(): boolean {
    return this._clickable;
  }

  supportPicking(): boolean {
    return this._supportPicking;
  }

  // Support textual search by operator
  supportSearch(): boolean {
    return false;
  }

  // Return slot age in seconds
  getSlotAge(slotNumber: number): number {
    if (this.objectsSlots != null)
      return Math.floor((Date.now() - this.objectsSlots[slotNumber].time) / 1000.0);
    else
      return undefined;
  }

  searchObjects(searchPattern: string): SearchResult[] {
    let lowerSearchPattern = searchPattern.toLocaleLowerCase();
    let results = [];
    if (this.supportSearch()) {
      this.allObjects.forEach((obj: AspObject) => {
        let identifiers = obj.getIdentifiers();
        let startsIdent = '';
        let anyIdent = '';
        for (let i = 0; i < identifiers.length; i++) {
          let ident;
          try {
            ident = identifiers[i].toLocaleLowerCase();
          }
          catch (err) {
            console.log("Failed toLocaleLowerCase with value: ", identifiers[i], ' error: ', err);
            continue;
          }
          if (ident.startsWith(lowerSearchPattern)) {
            startsIdent = identifiers[i];
            break;
          }
          else if (ident.search(lowerSearchPattern) != -1) {
            anyIdent = identifiers[i];
          }
        }
        if (startsIdent.length > 0 || anyIdent.length > 0) {
          let result = new SearchResult();
          result.obj = obj;
          result.identifier = startsIdent.length > 0 ? startsIdent : anyIdent;
          results.push(result);
        }
      });
    }
    return results;
  }

  // Return total objects count
  objectsCount(): number {
    return this.allObjects.size;
  }

  contextMenuActions(obj: AspObject): number[] {
    return [];
  }

  globalMenuActions(obj: AspObject): number[] {
    return [];
  }

  canBePicked(action: number, actionObj: AspObject, nextObj: AspObject): boolean {
    return false;
  }

  handleUserAction(action: number, obj: AspObject, mapMngr: IMapMngr): boolean {
    if (action == consts.USER_ACTION_ARO) {
      if (obj && obj.active()) {
        AppInjector.get(AroService).showTrackAro(obj.id);
      }
      return true;
    }
    // if(action == consts.USER_ACTION_CENTER){
    //   mapMngr.setMapCenter(obj.geometry);
    // }
    return false;
  }

  // handleUserAction(action: number, obj: AspObject, mapMngr: IMapMngr): boolean {
  //   if (action == consts.USER_ACTION_ARO) {
  //     if (obj && obj.active()) {
  //         if(obj.objectType == 'sensor-track')
  //         {
  //           let trk = obj as TrackObject;
  //           AppInjector.get(AroTrackService).showTrack(trk);
  //         }
  //         if(obj.objectType == 'plot')
  //         {
  //           AppInjector.get(AroService).showPlotAro(obj.id);
  //         }
  //     }
  //     return true;
  //   }
  //   return false;
  // }

  addSubscriber(subs: ObjectsPoolSubscriber) {
    const index = this.subscribers.indexOf(subs);
    if (index == -1)
      this.subscribers.push(subs);
  }

  delSubscriber(subs: ObjectsPoolSubscriber) {
    const index = this.subscribers.indexOf(subs);
    if (index > -1)
      this.subscribers.splice(index, 1);
  }

  getAllObjects(): Map<any, AspObject> {
    return this.allObjects;
  }

  hookObject(obj: AspObject): boolean {
    if (this.allObjects.has(obj.id)) {
      for (let i = 0; i < this.subscribers.length; i++)
        this.subscribers[i].onObjectHook(obj);
      return true;
    }
    else
      return false;
  }

  private notifyObjectCreate(obj: AspObject): void {
    for (let i = 0; i < this.subscribers.length; i++)
      this.subscribers[i].onObjectCreate(obj);
  }

  private notifyObjectDelete(obj: AspObject): void {
    for (let i = 0; i < this.subscribers.length; i++)
      this.subscribers[i].onObjectDelete(obj);
  }

  private notifySlotDelete(slotNumber: number): void {
    for (let i = 0; i < this.subscribers.length; i++)
      this.subscribers[i].onDeleteSlot(slotNumber);
  }

}
